import { useMemo } from 'react'
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts'
import type { CommessaStrategia } from './GanttCommesse'

interface Props {
  commesse: CommessaStrategia[]
  marginalita: { commessa_id: string; valore_offerta: number; margine_percentuale: number }[]
}

const fmt = (n: number) => n >= 1000 ? `€${(n / 1000).toFixed(0)}k` : `€${n.toFixed(0)}`

export default function RevenueForecast({ commesse, marginalita }: Props) {
  const today = new Date()

  const margMap = useMemo(() =>
    Object.fromEntries(marginalita.map(m => [m.commessa_id, m])),
    [marginalita]
  )

  const monthData = useMemo(() => {
    const byMonth: Record<string, { valore: number; margine: number; commesse: string[] }> = {}

    commesse.forEach(c => {
      const d = c.data_prevista_consegna
      if (!d) return
      const dt = new Date(d + 'T00:00:00')
      if (dt < today) return // Solo futuro
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
      if (!byMonth[key]) byMonth[key] = { valore: 0, margine: 0, commesse: [] }
      const fin = margMap[c.id]
      const val = fin?.valore_offerta || 0
      const marg = fin ? (val * fin.margine_percentuale / 100) : 0
      byMonth[key].valore += val
      byMonth[key].margine += marg
      byMonth[key].commesse.push(`${c.numero_commessa} ${c.titolo}`)
    })

    // Genera 12 mesi da oggi
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const label = d.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' })
      const data = byMonth[key] || { valore: 0, margine: 0, commesse: [] }
      return { key, label, ...data }
    })

    // Cumulativo
    let cumValore = 0
    let cumMargine = 0
    return months.map((m) => {
      cumValore += m.valore
      cumMargine += m.margine
      return { ...m, cum_valore: cumValore, cum_margine: cumMargine }
    })
  }, [commesse, margMap])

  const totale12m = monthData[monthData.length - 1]?.cum_valore || 0
  const margine12m = monthData[monthData.length - 1]?.cum_margine || 0
  const meseTop = [...monthData].sort((a, b) => b.valore - a.valore)[0]

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const d = monthData.find(m => m.label === label)
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow p-3 text-xs">
        <p className="font-bold mb-2">{label}</p>
        {payload.map((p: any) => (
          <p key={p.name} style={{ color: p.color }}>{p.name}: <strong>{fmt(p.value)}</strong></p>
        ))}
        {d && d.commesse.length > 0 && (
          <div className="mt-2 border-t border-gray-100 pt-2">
            {d.commesse.map((c, i) => <p key={i} className="text-gray-400">{c}</p>)}
          </div>
        )}
      </div>
    )
  }

  if (commesse.length === 0) {
    return <div className="text-center text-gray-400 py-16">Nessuna commessa con data di consegna definita</div>
  }

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Fatturato proiettato 12m</p>
          <p className="text-2xl font-bold text-gray-900">{fmt(totale12m)}</p>
          <p className="text-xs text-gray-400 mt-1">basato su date consegna</p>
        </div>
        <div className="bg-white border border-green-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Margine proiettato 12m</p>
          <p className="text-2xl font-bold text-green-600">{fmt(margine12m)}</p>
        </div>
        <div className="bg-white border border-blue-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 mb-1">Mese più intenso</p>
          <p className="text-xl font-bold text-blue-600">{meseTop?.label}</p>
          <p className="text-sm text-gray-500">{fmt(meseTop?.valore || 0)}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Proiezione fatturato mensile + cumulativo</h3>
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={monthData} margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" tickFormatter={fmt} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar yAxisId="left" dataKey="valore" name="Fatturato mensile" fill="#93c5fd" radius={[4, 4, 0, 0]} />
            <Bar yAxisId="left" dataKey="margine" name="Margine mensile" fill="#86efac" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="cum_valore" name="Cumulativo" stroke="#3b82f6" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly breakdown table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Mese</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Fatturato</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Margine</th>
              <th className="px-4 py-2 text-right font-medium text-gray-500">Cumulativo</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500">Commesse</th>
            </tr>
          </thead>
          <tbody>
            {monthData.filter(m => m.valore > 0).map((m) => (
              <tr key={m.key} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-700">{m.label}</td>
                <td className="px-4 py-2 text-right font-bold text-gray-900">{fmt(m.valore)}</td>
                <td className="px-4 py-2 text-right text-green-600">{fmt(m.margine)}</td>
                <td className="px-4 py-2 text-right text-blue-600">{fmt(m.cum_valore)}</td>
                <td className="px-4 py-2 text-gray-400">{m.commesse.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
